import jQuery from 'jquery';
import { updateFewGridCardsClasses } from './few-grid-cards';
import { eventedPushState } from '../vendor/evented-history-state-functions';
import { getUrlArray } from '../vendor/url-helper-functions';

const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;
const RELATED_STORAGE_KEY = 'ldc_team_hub_include_related';
const RELATED_PAGE_SIZE = 8;

let feedbackEl;
let statusEl;
let emptyEl;
let emptyTermEl;
let emptyCopyDefaultEl;
let emptyCopyWidenEl;
let fullSearchLinkEl;
let resetFilterEl;
let searchFormEl;
let teamSearchInput;
let includeRelatedEl;
let relatedSectionEl;
let relatedGridEl;
let relatedEmptyEl;
let relatedSummaryEl;
let relatedFullSearchEl;
let relatedLoadMoreEl;
let relatedLoadMoreBtn;
let didYouMeanEl;
let didYouMeanLinksEl;

let requestSeq = 0;
let lastRelatedItems = [];
let relatedRenderItems = [];
let relatedVisibleCount = RELATED_PAGE_SIZE;
let lastIdSet = null;
let lastIdOrder = [];
let originalCardOrder = null;
let activeController = null;
let teamNameIndex = null;
let searchLoading = false;

const PLACEHOLDER_IDLE = '';
const PLACEHOLDER_FOCUS = 'Search';
const ARIA_ALL = 'Search all team members';
const MAX_DID_YOU_MEAN = 3;

function getFilterSelectValue() {
	const sel = document.getElementById('filter-select-history');
	if (!sel || sel.selectedIndex < 0) {
		return 'all';
	}
	const v = sel.options[sel.selectedIndex].value;
	return !v || v === 'all' ? 'all' : v;
}

function searchQueryForMessage(rawQuery) {
	return rawQuery.trim().replace(/"/g, '\u2019');
}

function ensureSearchLoader() {
	if (!searchFormEl) {
		return;
	}
	if (!searchFormEl.querySelector('.hub-filter-loader')) {
		const loader = document.createElement('span');
		loader.className = 'hub-filter-loader';
		loader.setAttribute('aria-hidden', 'true');
		const wrap = searchFormEl.querySelector('.search-field-wrap') || searchFormEl;
		wrap.appendChild(loader);
	}
}

function setSearchLoading(isLoading) {
	searchLoading = Boolean(isLoading);
	ensureSearchLoader();
	if (!searchFormEl) {
		return;
	}
	searchFormEl.classList.toggle('is-filter-loading', searchLoading);
	searchFormEl.setAttribute('aria-busy', searchLoading ? 'true' : 'false');
}

function isRelatedFeatureEnabled() {
	const wrapper = document.getElementById('select-and-search-wrapper');
	if (!wrapper) {
		return Boolean(includeRelatedEl);
	}
	return wrapper.getAttribute('data-related-enabled') !== '0';
}

function isRelatedReasonsEnabled() {
	const wrapper = document.getElementById('select-and-search-wrapper');
	if (!wrapper) {
		return false;
	}
	return wrapper.getAttribute('data-related-show-reasons') === '1';
}

function getRelatedTitleTemplate() {
	const heading = document.getElementById('team-hub-related-heading');
	const template = heading && heading.getAttribute('data-title-template');
	return (template && template.trim()) || 'Content linked to {search_term}';
}

function updateRelatedTitle(rawQuery) {
	const heading = document.getElementById('team-hub-related-heading');
	if (!heading) {
		return;
	}
	const quoted = searchQueryForMessage(rawQuery);
	const term = quoted ? `“${quoted}”` : '…';
	heading.textContent = getRelatedTitleTemplate().replace(/\{search_term\}/gi, term);
}

function isRelatedEnabled() {
	if (!isRelatedFeatureEnabled()) {
		return false;
	}
	return Boolean(includeRelatedEl && includeRelatedEl.getAttribute('aria-pressed') === 'true');
}

function setRelatedEnabled(enabled) {
	if (!includeRelatedEl) {
		return;
	}
	includeRelatedEl.setAttribute('aria-pressed', enabled ? 'true' : 'false');
	includeRelatedEl.classList.toggle('is-active', Boolean(enabled));
}

function restoreRelatedPreference() {
	if (!includeRelatedEl) {
		return;
	}
	let enabled = false;
	try {
		enabled = window.localStorage.getItem(RELATED_STORAGE_KEY) === '1';
	} catch (e) {
		enabled = false;
	}
	setRelatedEnabled(enabled);
}

function persistRelatedPreference() {
	if (!includeRelatedEl) {
		return;
	}
	try {
		window.localStorage.setItem(RELATED_STORAGE_KEY, isRelatedEnabled() ? '1' : '0');
	} catch (e) {
		// Ignore storage failures (private mode, etc.).
	}
}

function syncTeamHubSearchPlaceholder() {
	if (!teamSearchInput) {
		return;
	}

	const isActive = document.activeElement === teamSearchInput;
	teamSearchInput.placeholder = isActive ? PLACEHOLDER_FOCUS : PLACEHOLDER_IDLE;

	const sel = document.getElementById('filter-select-history');
	if (!sel || sel.selectedIndex < 0) {
		teamSearchInput.setAttribute('aria-label', ARIA_ALL);
		return;
	}
	const opt = sel.options[sel.selectedIndex];
	const value = (opt && opt.value) || 'all';
	const label = opt && opt.textContent ? opt.textContent.replace(/\s+/g, ' ').trim() : '';

	if (!value || value === 'all') {
		teamSearchInput.setAttribute('aria-label', ARIA_ALL);
		return;
	}

	teamSearchInput.setAttribute(
		'aria-label',
		label
			? `Search team members with the ${label} filter applied. Results match your text within this list.`
			: ARIA_ALL
	);
}

function getCards() {
	return document.querySelectorAll('#team-grid .team-member[data-post-id]');
}

function getTeamGrid() {
	return document.getElementById('team-grid');
}

function rememberOriginalCardOrder() {
	if (originalCardOrder) {
		return;
	}
	originalCardOrder = Array.from(getCards());
}

function restoreOriginalCardOrder() {
	const grid = getTeamGrid();
	if (!grid || !originalCardOrder) {
		return;
	}
	originalCardOrder.forEach((el) => {
		if (el.isConnected || grid.contains(el)) {
			grid.appendChild(el);
		}
	});
}

/**
 * Reorder team cards to match ranked search IDs (given-name hits before surname prefixes).
 *
 * @param {number[]} orderedIds
 */
function reorderCardsBySearchIds(orderedIds) {
	const grid = getTeamGrid();
	if (!grid || !Array.isArray(orderedIds) || !orderedIds.length) {
		return;
	}

	rememberOriginalCardOrder();

	const byId = new Map();
	getCards().forEach((el) => {
		const id = parseInt(el.getAttribute('data-post-id'), 10);
		if (Number.isFinite(id)) {
			byId.set(id, el);
		}
	});

	const frag = document.createDocumentFragment();
	const used = new Set();

	orderedIds.forEach((id) => {
		const el = byId.get(id);
		if (!el || used.has(id)) {
			return;
		}
		frag.appendChild(el);
		used.add(id);
	});

	// Keep non-matching cards after matches (still in original relative order).
	if (originalCardOrder) {
		originalCardOrder.forEach((el) => {
			const id = parseInt(el.getAttribute('data-post-id'), 10);
			if (!Number.isFinite(id) || used.has(id)) {
				return;
			}
			frag.appendChild(el);
		});
	}

	grid.appendChild(frag);
}

function getVisibleTeamIds() {
	const ids = [];
	getCards().forEach((el) => {
		if (el.classList.contains('is-hidden') || el.classList.contains('team-fuzzy-hidden')) {
			return;
		}
		const id = parseInt(el.getAttribute('data-post-id'), 10);
		if (Number.isFinite(id)) {
			ids.push(id);
		}
	});
	return ids;
}

function countVisibleCards() {
	return getVisibleTeamIds().length;
}

function tokenizeName(value) {
	return String(value || '')
		.toLowerCase()
		.replace(/[^a-z0-9\s'-]/g, ' ')
		.split(/[\s'-]+/)
		.filter((token) => token.length >= 2);
}

function levenshtein(a, b) {
	const left = String(a || '');
	const right = String(b || '');
	if (left === right) {
		return 0;
	}
	if (!left.length) {
		return right.length;
	}
	if (!right.length) {
		return left.length;
	}

	const rows = left.length + 1;
	const cols = right.length + 1;
	const prev = new Array(cols);
	const curr = new Array(cols);
	for (let j = 0; j < cols; j += 1) {
		prev[j] = j;
	}
	for (let i = 1; i < rows; i += 1) {
		curr[0] = i;
		const ca = left.charCodeAt(i - 1);
		for (let j = 1; j < cols; j += 1) {
			const cost = ca === right.charCodeAt(j - 1) ? 0 : 1;
			curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
		}
		for (let j = 0; j < cols; j += 1) {
			prev[j] = curr[j];
		}
	}
	return prev[cols - 1];
}

function maxEditDistanceForToken(token) {
	const len = token.length;
	if (len <= 3) {
		return 1;
	}
	if (len <= 7) {
		return 2;
	}
	return 3;
}

function buildTeamNameIndex() {
	const entries = [];
	getCards().forEach((el) => {
		const titleEl = el.querySelector('.title');
		const name = titleEl ? titleEl.textContent.replace(/\s+/g, ' ').trim() : '';
		if (!name) {
			return;
		}
		const id = parseInt(el.getAttribute('data-post-id'), 10);
		entries.push({
			id: Number.isFinite(id) ? id : 0,
			name,
			tokens: tokenizeName(name),
			el,
		});
	});
	return entries;
}

function getTeamNameIndex() {
	if (!teamNameIndex) {
		teamNameIndex = buildTeamNameIndex();
	}
	return teamNameIndex;
}

function scoreNameSuggestion(entry, queryTokens) {
	if (!entry.tokens.length || !queryTokens.length) {
		return null;
	}

	let totalDistance = 0;
	for (const qToken of queryTokens) {
		const maxDist = maxEditDistanceForToken(qToken);
		let best = Infinity;

		for (const nameToken of entry.tokens) {
			if (nameToken === qToken) {
				best = 0;
				break;
			}
			if (nameToken.startsWith(qToken) || qToken.startsWith(nameToken)) {
				best = Math.min(best, 0);
				break;
			}
			const dist = levenshtein(qToken, nameToken);
			if (dist < best) {
				best = dist;
			}
		}

		if (!Number.isFinite(best) || best > maxDist) {
			return null;
		}
		totalDistance += best;
	}

	return totalDistance;
}

function findDidYouMeanSuggestions(rawQuery, { preferInFilter = true } = {}) {
	const queryTokens = tokenizeName(rawQuery);
	if (!queryTokens.length) {
		return [];
	}

	const scored = [];
	getTeamNameIndex().forEach((entry) => {
		const inFilter = entry.el && !entry.el.classList.contains('is-hidden');
		const distance = scoreNameSuggestion(entry, queryTokens);
		if (distance === null) {
			return;
		}
		// Exact / prefix matches are already handled by search — skip zero-distance.
		if (distance === 0) {
			return;
		}
		scored.push({
			name: entry.name,
			distance,
			inFilter: Boolean(inFilter),
		});
	});

	scored.sort((a, b) => {
		if (preferInFilter && a.inFilter !== b.inFilter) {
			return a.inFilter ? -1 : 1;
		}
		if (a.distance !== b.distance) {
			return a.distance - b.distance;
		}
		return a.name.localeCompare(b.name);
	});

	const seen = new Set();
	const suggestions = [];
	for (const item of scored) {
		const key = item.name.toLowerCase();
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		suggestions.push(item.name);
		if (suggestions.length >= MAX_DID_YOU_MEAN) {
			break;
		}
	}
	return suggestions;
}

function hideDidYouMean() {
	if (didYouMeanEl) {
		didYouMeanEl.hidden = true;
	}
	if (didYouMeanLinksEl) {
		didYouMeanLinksEl.innerHTML = '';
	}
}

function renderDidYouMean(suggestions) {
	if (!didYouMeanEl || !didYouMeanLinksEl) {
		return;
	}
	if (!Array.isArray(suggestions) || !suggestions.length) {
		hideDidYouMean();
		return;
	}

	didYouMeanLinksEl.innerHTML = suggestions
		.map((name, index) => {
			const safe = escapeHtml(name);
			const sep =
				index > 0
					? `<span class="team-hub-search-feedback__did-you-mean-sep" aria-hidden="true">${
							index === suggestions.length - 1 ? ' or ' : ', '
						}</span>`
					: '';
			return (
				`${sep}<button type="button" class="team-hub-search-feedback__did-you-mean-link" data-suggestion="${safe}">${safe}</button>`
			);
		})
		.join('');
	didYouMeanEl.hidden = false;
}

function applyDidYouMeanSuggestion(name) {
	if (!teamSearchInput || !name) {
		return;
	}
	teamSearchInput.value = name;
	teamSearchInput.focus();
	applyTextFilter(name);
}

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function setRelatedToggleVisible(visible) {
	if (!includeRelatedEl || !isRelatedFeatureEnabled()) {
		if (includeRelatedEl) {
			includeRelatedEl.hidden = true;
		}
		return;
	}
	includeRelatedEl.hidden = !visible;
}

function pushTeamHubFilterAllUrl() {
	const urlArray = getUrlArray();
	if (!urlArray || urlArray.length < 4) {
		return;
	}
	const nextUrl = `/${urlArray[3]}/`;
	const currentPath = window.location.pathname.replace(/\/?$/, '/');
	if (currentPath === nextUrl) {
		return;
	}
	eventedPushState({ filterby: 'all', filter: 'all' }, '', nextUrl);
}

function resetTeamHubFilterToAll() {
	const sel = document.getElementById('filter-select-history');
	if (!sel) {
		return;
	}

	const $sel = jQuery(sel);
	$sel.val('all');

	// Clear taxonomy filtering immediately — don't rely on pushstate/change timing.
	jQuery('.filter-item').removeClass('is-hidden');
	updateFewGridCardsClasses(jQuery('#team-grid'), '.team-member');

	// Canonical All URL is /team-hub/ (see rewrite rules).
	pushTeamHubFilterAllUrl();

	syncTeamHubSearchPlaceholder();
	reapplyCurrentSearchUi(teamSearchInput ? teamSearchInput.value : '');
}

function setResetFilterVisible(visible) {
	if (!resetFilterEl) {
		return;
	}
	resetFilterEl.hidden = !visible;
}

function setSearchingUi(active) {
	if (!feedbackEl || !statusEl || !emptyEl) {
		return;
	}
	if (active) {
		feedbackEl.hidden = false;
		emptyEl.hidden = true;
		statusEl.textContent = 'Searching…';
		setRelatedToggleVisible(false);
		hideDidYouMean();
	}
}

function updateFullSearchLinks(rawQuery) {
	const q = rawQuery.trim();
	if (!q || !searchFormEl) {
		return;
	}
	const u = new URL(searchFormEl.action || window.location.origin);
	u.searchParams.set('searchfilter', 'team');
	u.searchParams.set('s', q);
	const href = u.toString();
	if (fullSearchLinkEl) {
		fullSearchLinkEl.setAttribute('href', href);
	}
	if (relatedFullSearchEl) {
		relatedFullSearchEl.setAttribute('href', href);
	}
}

function formatRelatedCount(label, count) {
	const lower = String(label || 'item').toLowerCase();
	if (count === 1) {
		return `1 ${lower}`;
	}
	if (lower === 'case study' || lower === 'portfolio') {
		return count === 1 ? '1 portfolio' : `${count} portfolios`;
	}
	if (lower === 'news') {
		return `${count} news items`;
	}
	if (lower.endsWith('s')) {
		return `${count} ${lower}`;
	}
	return `${count} ${lower}s`;
}

function hideRelatedSection() {
	if (relatedSectionEl) {
		relatedSectionEl.hidden = true;
	}
	if (relatedGridEl) {
		relatedGridEl.innerHTML = '';
	}
	if (relatedEmptyEl) {
		relatedEmptyEl.hidden = true;
	}
	if (relatedSummaryEl) {
		relatedSummaryEl.textContent = '';
	}
	if (relatedLoadMoreEl) {
		relatedLoadMoreEl.hidden = true;
	}
	relatedRenderItems = [];
	relatedVisibleCount = RELATED_PAGE_SIZE;
}

function updateRelatedLoadMore() {
	if (!relatedLoadMoreEl) {
		return;
	}
	relatedLoadMoreEl.hidden = relatedVisibleCount >= relatedRenderItems.length;
}

function paintRelatedGrid() {
	if (!relatedGridEl) {
		return;
	}
	const visible = relatedRenderItems.slice(0, relatedVisibleCount);
	relatedGridEl.innerHTML = visible.map(renderRelatedCard).join('');
	updateRelatedLoadMore();
}

function loadMoreRelated() {
	if (relatedVisibleCount >= relatedRenderItems.length) {
		return;
	}
	relatedVisibleCount = Math.min(
		relatedVisibleCount + RELATED_PAGE_SIZE,
		relatedRenderItems.length
	);
	paintRelatedGrid();
	updateRelatedSummaryCounts();
}

function updateRelatedSummaryCounts() {
	if (!relatedSummaryEl || !relatedRenderItems.length) {
		return;
	}
	const visible = relatedRenderItems.slice(0, relatedVisibleCount);
	const counts = visible.reduce((acc, item) => {
		const key = item.typeLabel || item.type || 'Content';
		acc[key] = (acc[key] || 0) + 1;
		return acc;
	}, {});
	const countParts = Object.keys(counts).map((label) => formatRelatedCount(label, counts[label]));

	const people = [];
	const seenPeople = new Set();
	visible.forEach((item) => {
		(item.relationships || []).forEach((rel) => {
			const name = (rel.name || '').trim();
			const key = String(rel.teamId || name);
			if (name && !seenPeople.has(key)) {
				seenPeople.add(key);
				people.push(rel);
			}
		});
	});

	const total = relatedRenderItems.length;
	const shown = visible.length;
	const moreNote = total > shown ? ` Showing ${shown} of ${total}.` : '';
	relatedSummaryEl.innerHTML = `Showing ${countParts.join(', ')} linked to ${formatPeopleClauseHtml(people)}.${moreNote}`;
}

function filterRelatedForVisibleTeams(relatedItems, visibleTeamIds) {
	if (!Array.isArray(relatedItems) || !relatedItems.length) {
		return [];
	}
	const visible = new Set(visibleTeamIds);
	return relatedItems
		.map((item) => {
			const relationships = Array.isArray(item.relationships) ? item.relationships : [];
			const filteredRels = relationships.filter((rel) =>
				visible.has(parseInt(rel.teamId, 10))
			);
			const teamIds = Array.isArray(item.teamIds) ? item.teamIds : [];
			const matchesVisible =
				filteredRels.length > 0 ||
				(!relationships.length &&
					(!teamIds.length || teamIds.some((id) => visible.has(parseInt(id, 10)))));

			if (!matchesVisible) {
				return null;
			}

			return {
				...item,
				relationships: filteredRels.length ? filteredRels : relationships,
				teamIds: filteredRels.length
					? filteredRels.map((rel) => parseInt(rel.teamId, 10))
					: teamIds,
			};
		})
		.filter(Boolean);
}

function personProfileLinkHtml(rel) {
	const name = escapeHtml(rel.name || 'Team member');
	const url = rel.url ? escapeHtml(rel.url) : '';
	if (!url) {
		return `<span class="team-hub-related__person">${name}</span>`;
	}
	return (
		`<a class="team-hub-related__person-link" href="${url}" target="_blank" rel="noopener noreferrer">${name}</a>`
	);
}

function formatPeopleClauseHtml(people) {
	if (!people.length) {
		return 'the people above';
	}
	const links = people.map(personProfileLinkHtml);
	if (links.length === 1) {
		return links[0];
	}
	if (links.length === 2) {
		return `${links[0]} and ${links[1]}`;
	}
	return `${links.slice(0, -1).join(', ')} and ${links[links.length - 1]}`;
}

function renderRelationshipTokens(item) {
	const relationships = Array.isArray(item.relationships) ? item.relationships : [];
	if (!relationships.length) {
		return '';
	}

	const showReasons = isRelatedReasonsEnabled();
	const tokens = relationships
		.slice(0, 3)
		.map((rel) => {
			const reason = escapeHtml(rel.reasonLabel || rel.reason || 'Linked');
			const reasonKey = escapeHtml(rel.reason || 'linked');
			const reasonHtml = showReasons
				? `<span class="team-hub-related__token-sep" aria-hidden="true">·</span>` +
					`<span class="team-hub-related__token-reason">${reason}</span>`
				: '';
			return (
				`<span class="team-hub-related__token team-hub-related__token--${reasonKey}">` +
				`<span class="team-hub-related__token-name">${personProfileLinkHtml(rel)}</span>` +
				reasonHtml +
				`</span>`
			);
		})
		.join('');

	const more =
		relationships.length > 3
			? `<span class="team-hub-related__token team-hub-related__token--more">+${relationships.length - 3}</span>`
			: '';

	return `<div class="team-hub-related__footer">${tokens}${more}</div>`;
}

function renderRelatedCard(item) {
	const title = escapeHtml(item.title || '');
	const link = escapeHtml(item.link || '#');
	const typeLabel = escapeHtml(item.typeLabel || item.type || 'Content');
	const type = escapeHtml(item.type || '');
	const excerpt = escapeHtml(item.excerpt || '');
	const date = escapeHtml(item.date || '');

	const metaParts = [typeLabel];
	if (date) {
		metaParts.push(date);
	}

	const excerptHtml = excerpt
		? `<p class="team-hub-related__excerpt">${excerpt}</p>`
		: '';

	return (
		`<article class="team-hub-related__card" data-post-type="${type}">` +
		`<div class="team-hub-related__inner">` +
		`<div class="team-hub-related__content">` +
		`<div class="team-hub-related__body">` +
		`<p class="team-hub-related__meta">${metaParts.join(' · ')}</p>` +
		`<h3 class="team-hub-related__card-title"><a class="team-hub-related__card-link" href="${link}">${title}</a></h3>` +
		excerptHtml +
		`</div>` +
		renderRelationshipTokens(item) +
		`</div>` +
		`</div>` +
		`</article>`
	);
}

function getRelatedForVisibleTeams(relatedItems) {
	return filterRelatedForVisibleTeams(relatedItems, getVisibleTeamIds());
}

function hasRelatedForVisibleTeams(relatedItems) {
	return getRelatedForVisibleTeams(relatedItems).length > 0;
}

function renderRelatedSection(rawQuery, relatedItems) {
	if (!relatedSectionEl || !relatedGridEl) {
		return;
	}

	if (!isRelatedEnabled() || !rawQuery.trim()) {
		hideRelatedSection();
		return;
	}

	const filtered = getRelatedForVisibleTeams(relatedItems);
	if (!filtered.length) {
		hideRelatedSection();
		return;
	}

	relatedSectionEl.hidden = false;
	updateRelatedTitle(rawQuery);
	updateFullSearchLinks(rawQuery);

	if (relatedEmptyEl) {
		relatedEmptyEl.hidden = true;
	}

	relatedRenderItems = filtered;
	relatedVisibleCount = RELATED_PAGE_SIZE;
	paintRelatedGrid();
	updateRelatedSummaryCounts();
}

function updateSearchFeedback(rawQuery, options = {}) {
	if (!feedbackEl || !statusEl || !emptyEl) {
		return;
	}

	const q = rawQuery.trim();
	const { errorMessage, suggestWidenFilter, didYouMean } = options;

	if (errorMessage) {
		feedbackEl.hidden = false;
		emptyEl.hidden = true;
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = false;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = true;
		}
		setResetFilterVisible(false);
		statusEl.textContent = errorMessage;
		setRelatedToggleVisible(false);
		hideDidYouMean();
		hideRelatedSection();
		return;
	}

	if (!q) {
		feedbackEl.hidden = true;
		statusEl.textContent = '';
		emptyEl.hidden = true;
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = false;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = true;
		}
		setResetFilterVisible(false);
		setRelatedToggleVisible(false);
		hideDidYouMean();
		hideRelatedSection();
		return;
	}

	const visible = countVisibleCards();

	if (visible === 0) {
		feedbackEl.hidden = true;
		statusEl.textContent = '';
		emptyEl.hidden = false;
		const widen = Boolean(suggestWidenFilter);
		if (emptyCopyDefaultEl) {
			emptyCopyDefaultEl.hidden = widen;
		}
		if (emptyCopyWidenEl) {
			emptyCopyWidenEl.hidden = !widen;
		}
		setResetFilterVisible(widen);
		if (emptyTermEl) {
			emptyTermEl.textContent = q;
		}
		updateFullSearchLinks(rawQuery);
		setRelatedToggleVisible(false);
		renderDidYouMean(Array.isArray(didYouMean) ? didYouMean : []);
		hideRelatedSection();
	} else {
		emptyEl.hidden = true;
		setResetFilterVisible(false);
		feedbackEl.hidden = false;
		const quoted = searchQueryForMessage(rawQuery);
		if (visible === 1) {
			statusEl.textContent = `1 person matches “${quoted}”`;
		} else {
			statusEl.textContent = `${visible} people match “${quoted}”`;
		}
		setRelatedToggleVisible(isRelatedFeatureEnabled() && hasRelatedForVisibleTeams(lastRelatedItems));
		hideDidYouMean();
		renderRelatedSection(rawQuery, lastRelatedItems);
	}
}

function applyVisibilityFromIds(idSet, orderedIds = []) {
	if (Array.isArray(orderedIds) && orderedIds.length) {
		reorderCardsBySearchIds(orderedIds);
	}
	getCards().forEach((el) => {
		const id = parseInt(el.getAttribute('data-post-id'), 10);
		const inResults = Number.isFinite(id) && idSet.has(id);
		const taxOk = !el.classList.contains('is-hidden');
		if (inResults && taxOk) {
			el.classList.remove('team-fuzzy-hidden');
		} else {
			el.classList.add('team-fuzzy-hidden');
		}
	});
	updateFewGridCardsClasses(jQuery('#team-grid'), '.team-member');
}

function clearTextFilter() {
	lastIdSet = null;
	lastIdOrder = [];
	lastRelatedItems = [];
	restoreOriginalCardOrder();
	getCards().forEach((el) => el.classList.remove('team-fuzzy-hidden'));
	updateFewGridCardsClasses(jQuery('#team-grid'), '.team-member');
	hideDidYouMean();
	hideRelatedSection();
}

function reapplyCurrentSearchUi(rawQuery) {
	const q = rawQuery.trim();
	if (!q) {
		clearTextFilter();
		updateSearchFeedback(rawQuery);
		return;
	}
	if (lastIdSet) {
		applyVisibilityFromIds(lastIdSet, lastIdOrder);
		const visible = countVisibleCards();
		const suggestWidenFilter =
			visible === 0 && lastIdSet.size > 0 && getFilterSelectValue() !== 'all';
		const didYouMean =
			visible === 0 && lastIdSet.size === 0 ? findDidYouMeanSuggestions(q) : [];
		updateSearchFeedback(rawQuery, { suggestWidenFilter, didYouMean });
		return;
	}
	applyTextFilter(rawQuery);
}

async function fetchTeamHubSearch(query) {
	const ajax = window.ldc_ajax_object;
	if (!ajax?.ajax_url || !ajax?.security) {
		throw new Error('Missing AJAX configuration');
	}

	if (activeController) {
		activeController.abort();
	}
	activeController = new AbortController();

	const body = new URLSearchParams();
	body.set('action', 'ldc_team_hub_relevanssi_search');
	body.set('security', ajax.security);
	body.set('search_term', query);
	body.set('include_related', isRelatedFeatureEnabled() ? '1' : '0');

	const res = await fetch(ajax.ajax_url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
		body,
		credentials: 'same-origin',
		signal: activeController.signal,
	});

	const data = await res.json();
	if (!data.success || !Array.isArray(data.data?.ids)) {
		const message =
			(data.data && data.data.message) ||
			(typeof data.data === 'string' ? data.data : '') ||
			(res.status === 429
				? 'Too many searches. Please wait a moment and try again.'
				: 'Search failed');
		throw new Error(message);
	}

	return {
		ids: data.data.ids.map((id) => parseInt(id, 10)).filter((id) => Number.isFinite(id)),
		related: Array.isArray(data.data.related) ? data.data.related : [],
	};
}

async function applyTextFilter(rawQuery) {
	const query = rawQuery.trim();
	const seq = ++requestSeq;

	if (!query) {
		if (activeController) {
			activeController.abort();
			activeController = null;
		}
		setSearchLoading(false);
		clearTextFilter();
		updateSearchFeedback(rawQuery);
		return;
	}

	if (query.length < MIN_QUERY_LENGTH) {
		if (activeController) {
			activeController.abort();
			activeController = null;
		}
		setSearchLoading(false);
		clearTextFilter();
		if (feedbackEl && statusEl) {
			feedbackEl.hidden = false;
			statusEl.textContent = 'Keep typing to search…';
		}
		if (emptyEl) {
			emptyEl.hidden = true;
		}
		setRelatedToggleVisible(false);
		hideDidYouMean();
		hideRelatedSection();
		return;
	}

	if (!window.ldc_ajax_object?.ajax_url) {
		setSearchLoading(false);
		updateSearchFeedback(rawQuery, { errorMessage: 'Search is not available on this page.' });
		return;
	}

	setSearchLoading(true);
	if (feedbackEl && statusEl) {
		feedbackEl.hidden = false;
		statusEl.textContent = 'Searching…';
	}
	if (emptyEl) {
		emptyEl.hidden = true;
	}
	hideDidYouMean();

	try {
		const result = await fetchTeamHubSearch(query);
		if (seq !== requestSeq) {
			return;
		}
		lastIdOrder = result.ids;
		lastIdSet = new Set(result.ids);
		lastRelatedItems = result.related;
		applyVisibilityFromIds(lastIdSet, lastIdOrder);
		const visible = countVisibleCards();
		const suggestWidenFilter =
			visible === 0 && lastIdSet.size > 0 && getFilterSelectValue() !== 'all';
		const didYouMean =
			visible === 0 && lastIdSet.size === 0 ? findDidYouMeanSuggestions(query) : [];
		updateSearchFeedback(rawQuery, { suggestWidenFilter, didYouMean });
	} catch (e) {
		if (e?.name === 'AbortError' || seq !== requestSeq) {
			return;
		}
		clearTextFilter();
		updateSearchFeedback(rawQuery, {
			errorMessage: 'We could not run the search just now. Please try again.',
		});
	} finally {
		if (seq === requestSeq) {
			setSearchLoading(false);
		}
	}
}

function debounce(fn, ms) {
	let t;
	return function debounced() {
		clearTimeout(t);
		t = setTimeout(fn, ms);
	};
}

function init() {
	const teamFilterField = document.querySelector(
		'#select-and-search-wrapper input[name="searchfilter"][value="team"]'
	);
	const form = teamFilterField ? teamFilterField.closest('form') : null;
	if (!form || !document.querySelector('#team-grid')) {
		return;
	}

	searchFormEl = form;
	ensureSearchLoader();
	feedbackEl = document.getElementById('team-hub-search-feedback');
	statusEl = document.getElementById('team-hub-search-status');
	emptyEl = document.getElementById('team-hub-search-empty');
	emptyTermEl = document.getElementById('team-hub-search-empty-term');
	emptyCopyDefaultEl = document.getElementById('team-hub-empty-copy-default');
	emptyCopyWidenEl = document.getElementById('team-hub-empty-copy-widen');
	fullSearchLinkEl = document.getElementById('team-hub-full-search-link');
	resetFilterEl = document.getElementById('team-hub-reset-filter');
	includeRelatedEl = document.getElementById('team-hub-include-related');
	relatedSectionEl = document.getElementById('team-hub-related');
	relatedGridEl = document.getElementById('team-hub-related-grid');
	relatedEmptyEl = document.getElementById('team-hub-related-empty');
	relatedSummaryEl = document.getElementById('team-hub-related-summary');
	relatedFullSearchEl = document.getElementById('team-hub-related-full-search');
	relatedLoadMoreEl = document.getElementById('team-hub-related-load-more');
	relatedLoadMoreBtn = document.getElementById('team-hub-related-load-more-btn');
	didYouMeanEl = document.getElementById('team-hub-did-you-mean');
	didYouMeanLinksEl = document.getElementById('team-hub-did-you-mean-links');

	const input = form.querySelector('input[name="s"]');
	if (!input) {
		return;
	}

	teamSearchInput = input;
	rememberOriginalCardOrder();
	teamNameIndex = buildTeamNameIndex();
	restoreRelatedPreference();
	syncTeamHubSearchPlaceholder();

	teamSearchInput.addEventListener('focus', syncTeamHubSearchPlaceholder);
	teamSearchInput.addEventListener('blur', syncTeamHubSearchPlaceholder);

	const runDebounced = debounce(() => {
		applyTextFilter(teamSearchInput.value);
	}, DEBOUNCE_MS);

	teamSearchInput.addEventListener('input', () => {
		if (!teamSearchInput.value.trim()) {
			requestSeq += 1;
			applyTextFilter('');
		} else {
			setSearchingUi(true);
			runDebounced();
		}
	});

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		applyTextFilter(teamSearchInput.value);
	});

	if (didYouMeanLinksEl) {
		didYouMeanLinksEl.addEventListener('click', (e) => {
			const button = e.target.closest('.team-hub-search-feedback__did-you-mean-link');
			if (!button) {
				return;
			}
			e.preventDefault();
			applyDidYouMeanSuggestion(button.getAttribute('data-suggestion') || button.textContent || '');
		});
	}
	if (resetFilterEl) {
		resetFilterEl.addEventListener('click', () => {
			resetTeamHubFilterToAll();
		});
	}
	if (relatedLoadMoreBtn) {
		relatedLoadMoreBtn.addEventListener('click', () => {
			loadMoreRelated();
		});
	}
	if (includeRelatedEl) {
		includeRelatedEl.addEventListener('click', () => {
			const enabling = !isRelatedEnabled();
			setRelatedEnabled(enabling);
			persistRelatedPreference();

			const q = teamSearchInput.value.trim();
			if (!q || q.length < MIN_QUERY_LENGTH || !hasRelatedForVisibleTeams(lastRelatedItems)) {
				hideRelatedSection();
				return;
			}

			if (!enabling) {
				hideRelatedSection();
				return;
			}

			renderRelatedSection(q, lastRelatedItems);
		});
	}

	jQuery('#filter-select-history').on('change', () => {
		syncTeamHubSearchPlaceholder();
	});

	jQuery(window).on('pushstate popstate', () => {
		// filterItems() also listens here; wait a tick so is-hidden is up to date.
		window.setTimeout(() => {
			syncTeamHubSearchPlaceholder();
			reapplyCurrentSearchUi(teamSearchInput.value);
		}, 0);
	});

	applyTextFilter(teamSearchInput.value);
}

jQuery(init);
